import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, ArrowRight, X } from 'lucide-react';
import { InlineWidget } from 'react-calendly';

/*
 * ============================================================
 * CALENDLY CONFIGURATION
 * ============================================================
 * Replace the URL below with your real Calendly scheduling link.
 * Format: https://calendly.com/YOUR_USERNAME/EVENT_TYPE
 * Example: https://calendly.com/medialabs/strategy-sync
 * ============================================================
 */
const CALENDLY_URL = 'https://calendly.com/innovision0108/30min';

/* ── Calendly Booking Modal (exported for reuse) ── */
export const CalendlyModal = ({ isOpen, onClose }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="calendly-modal-overlay"
      >
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl border border-[color:var(--hairline)] overflow-hidden"
          style={{ height: 'min(90vh, 750px)' }}
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          data-testid="calendly-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-[color:var(--hairline)] bg-white z-10 relative">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-brand-orange/10 flex items-center justify-center">
                <Calendar className="w-4 h-4 text-brand-orange" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-foreground">Strategy Sync</h3>
                <p className="text-[11px] text-muted-foreground">30-min consultation with MediaLabs</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-secondary transition-colors duration-200"
              data-testid="calendly-modal-close"
              aria-label="Close booking modal"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>

          {/* Calendly Embed */}
          <div className="w-full" style={{ height: 'calc(100% - 52px)' }}>
            <InlineWidget
              url={CALENDLY_URL}
              styles={{ height: '100%', width: '100%' }}
              pageSettings={{
                backgroundColor: 'ffffff',
                hideEventTypeDetails: false,
                hideLandingPageDetails: false,
                primaryColor: '1EACEE',
                textColor: '1e293b',
              }}
            />
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

/* ── Schedule CTA Section ── */
export const ScheduleSection = () => {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <section
        id="schedule"
        className="relative py-16 sm:py-20 lg:py-24 overflow-hidden"
        data-testid="schedule-section"
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(600px 400px at 50% 50%, rgba(246, 141, 44, 0.06), transparent 70%)' }}
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-brand-orange/8 border border-brand-orange/15 text-xs font-semibold text-brand-orange uppercase tracking-wider mb-6">
              <Calendar className="w-3.5 h-3.5" /> Free Strategy Session
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground mb-4" data-testid="schedule-headline">
              Ready to Start Your 15-Day Sprint?
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-8 max-w-lg mx-auto">
              Book a 30-minute strategy sync with our team. We'll scope your project, define the roadmap, and show you exactly how we deliver V1 in record time.
            </p>
            <motion.button
              onClick={() => setModalOpen(true)}
              whileHover={{ y: -2, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="pulse-orange inline-flex items-center justify-center gap-2.5 rounded-xl bg-brand-orange text-white text-base sm:text-lg font-bold px-8 py-4 min-h-[52px] shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-lift)] hover:bg-brand-orange-dark transition-shadow transition-colors duration-200"
              data-testid="schedule-cta-button"
            >
              <Calendar className="w-5 h-5" />
              Schedule a Strategy Sync
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      <CalendlyModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
};
